// src/components/applications/ApplicationLoginBlock.js
import React, { useState, useContext, memo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Chip from "@mui/material/Chip";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import {
  useSignupUserMutation,
  useGenAppLoginMutation,
  useForgotPasswordMutation,
} from "../../services/auth";
import { useGetAdminStatusQuery } from "../../services/admin/admin";
import { AuthContext } from "../../context/AuthContext";
import { NotificationContext } from "../../context/NotificationContext";
import { SettingsContext } from "../../context/SettingsContext";
import { formattedOpenDate } from "../utils/dateHelpers";
import FormSelect from "../helpers/FormSelect";

const isBlockedEmailDomain = (value = "") => {
  const email = value.trim().toLowerCase();
  // blocks learn68.ca and anything like student.learn68.ca
  return /@([a-z0-9-]+\.)*learn68\.ca$/.test(email);
};

function ApplicationLoginBlock() {
  const navigate = useNavigate();
  const { genAppLoginAuthContext, isAuthenticated } = useContext(AuthContext);
  const { addNotification } = useContext(NotificationContext);

  const { settings, isAppOpen, isGenAppOpen, isDASAppOpen } =
    useContext(SettingsContext);

  const [isSignup, setIsSignup] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [schoolId, setSchoolId] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] =
    useState(false);

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotEmailError, setForgotEmailError] = useState(false);
  const [, setForgotPasswordSent] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const [signupUser, { isLoading: isSignupLoading }] = useSignupUserMutation();
  const [genAppLogin, { isLoading }] = useGenAppLoginMutation();
  const [forgotPassword, { isLoading: isForgotPasswordLoading }] =
    useForgotPasswordMutation();

  const { refetch: refetchAdminStatus } = useGetAdminStatusQuery(undefined, {
    skip: !isAuthenticated,
  });

  const generalOpen = settings["GeneralOpen"]
    ? settings["GeneralOpen"].settingDate
    : null;
  const dasOpen = settings["DASOpen"] ? settings["DASOpen"].settingDate : null;

  // Refs for the actual input elements so browser autofill is reflected in UI state.
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  const emailStateRef = useRef("");
  const passwordStateRef = useRef("");

  useEffect(() => {
    emailStateRef.current = email;
    passwordStateRef.current = password;
  }, [email, password]);

  const syncLoginAutofillState = () => {
    const emailField = emailInputRef.current;
    const passwordField = passwordInputRef.current;

    const autofilledEmail = emailField?.value || "";
    const autofilledPassword = passwordField?.value || "";

    if (autofilledEmail !== emailStateRef.current) {
      emailStateRef.current = autofilledEmail;
      setEmail(autofilledEmail);
      setEmailError(
        Boolean(autofilledEmail) &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(autofilledEmail),
      );
    }

    if (autofilledPassword !== passwordStateRef.current) {
      passwordStateRef.current = autofilledPassword;
      setPassword(autofilledPassword);
      setPasswordError(
        Boolean(autofilledPassword) && autofilledPassword.trim() === "",
      );
    }
  };

  const handleLoginAutofillAnimationStart = () => {
    syncLoginAutofillState();
  };

  // Keep checking while the login form is mounted because browsers may autofill late.
  useEffect(() => {
    const syncOnVisibility = () => {
      if (document.visibilityState === "visible") {
        syncLoginAutofillState();
      }
    };

    syncLoginAutofillState();

    const animationFrames = [0, 100, 300, 1000, 2000].map((delay) =>
      window.setTimeout(syncLoginAutofillState, delay)
    );
    const intervalId = window.setInterval(syncLoginAutofillState, 500);

    window.addEventListener("focus", syncLoginAutofillState);
    window.addEventListener("pageshow", syncLoginAutofillState);
    document.addEventListener("visibilitychange", syncOnVisibility);

    return () => {
      animationFrames.forEach((timerId) => window.clearTimeout(timerId));
      window.clearInterval(intervalId);
      window.removeEventListener("focus", syncLoginAutofillState);
      window.removeEventListener("pageshow", syncLoginAutofillState);
      document.removeEventListener("visibilitychange", syncOnVisibility);
    };
  }, []);

  const loginEmailValue = email || emailInputRef.current?.value || "";
  const loginPasswordValue = password || passwordInputRef.current?.value || "";
  const isLoginDisabled =
    !loginEmailValue ||
    !loginPasswordValue ||
    emailError ||
    passwordError ||
    isLoading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const submittedEmail = loginEmailValue.trim();
    const submittedPassword = loginPasswordValue;

    if (!submittedEmail || !submittedPassword) {
      addNotification(`Email and Password are required`, "error");
      return;
    }
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(submittedEmail);
    const isPasswordValid = submittedPassword?.trim() !== "";

    if (!isEmailValid || !isPasswordValid) {
      setEmailError(!isEmailValid);
      setPasswordError(!isPasswordValid);
      addNotification(`Email or Password are not valid`, "error");
      return;
    }

    try {
      setEmail(submittedEmail);
      setPassword(submittedPassword);

      const result = await genAppLogin({
        email: submittedEmail,
        password: submittedPassword,
      }).unwrap();
      genAppLoginAuthContext(result.token, {
        ...result.user,
        email: result.user?.email || submittedEmail,
      });
      addNotification("Login successful!", "success");
      setIsRedirecting(true);

      if (isAuthenticated) refetchAdminStatus?.();

      if (result.user.forcechangepw) {
        navigate("/change-password", { replace: true });
      } else if (result.user?.isAdmin && result.user?.admin_type_id) {
        navigate("/admin", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    } catch (error) {
      const errorMessage =
        error?.data?.message || "An unexpected error occurred.";
      addNotification(`Error: ${errorMessage}`, "error");
      setIsRedirecting(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !confirmEmail.trim() ||
      !password.trim() ||
      !confirmPassword.trim() ||
      !schoolId
    ) {
      addNotification("All fields are required.", "error");
      return;
    }
    if (isBlockedEmailDomain(email) || isBlockedEmailDomain(confirmEmail)) {
      addNotification(
        "Please use a personal email address (learn68.ca school emails are not allowed).",
        "error"
      );
      return;
    }

    if (email.toLowerCase() !== confirmEmail.toLowerCase()) {
      addNotification("Emails do not match.", "error");
      return;
    }
    if (password !== confirmPassword) {
      addNotification("Passwords do not match.", "error");
      return;
    }

    try {
      const result = await signupUser({
        firstName,
        lastName,
        email,
        password,
        schoolId,
      }).unwrap();
      addNotification(result.message || "Signup successful!", "success");
      setIsSignup(false);
    } catch (error) {
      const errMsg = error?.data?.message || "Signup failed.";
      addNotification(`Error: ${errMsg}`, "error");
    }
  };

  const handleSubmitForgotPassword = async (e) => {
    e.preventDefault();

    if (!forgotEmail) {
      setForgotEmailError(true);
      addNotification("Email is required.", "error");
      return;
    }
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail);
    if (!isEmailValid) {
      setForgotEmailError(true);
      addNotification("Please enter a valid email.", "error");
      return;
    }

    setForgotEmailError(false);

    try {
      const response = await forgotPassword(forgotEmail).unwrap();
      addNotification(response.message, "success");
      setForgotPasswordSent(true);
      setIsForgotPassword(false);
    } catch (error) {
      const errorMessage =
        error?.data?.message || "An unexpected error occurred.";
      addNotification(`Error: ${errorMessage}`, "error");
    }
  };

  const showRightInfoPanel = !isSignup;

  const title = isSignup
    ? "Create an Account"
    : isForgotPassword
    ? "Forgot Password"
    : "Login";

  const subtitle = isSignup
    ? "Create one account to apply for awards."
    : isForgotPassword
    ? "Enter your email and we’ll send reset instructions."
    : "Sign in to access your application.";

  const headerIcon = isSignup ? (
    <PersonAddAltIcon />
  ) : isForgotPassword ? (
    <HelpOutlineIcon />
  ) : (
    <LockOutlinedIcon />
  );

  const primaryButtonSx = {
    borderRadius: 2,
    py: 1.2,
    fontWeight: 800,
    textTransform: "none",
  };

  const secondaryButtonSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 700,
  };

  return (
    <Box
      className="application-login"
      sx={{
        maxWidth: 1100,
        mx: "auto",
        px: { xs: 2, sm: 2 },
        py: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "440px 1fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        {/* LEFT: Auth panel */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
          }}
        >
          {/* Header band */}
          <Box
            sx={{
              px: 2.25,
              py: 2,
              background:
                "linear-gradient(135deg, rgba(93,193,175,0.20), rgba(217,83,79,0.10))",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: "rgba(255,255,255,0.8)",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                {headerIcon}
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 900, lineHeight: 1 }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  {subtitle}
                </Typography>
              </Box>

              {!isSignup && (
                <Chip
                  size="small"
                  label={
                    isAppOpen ? "Applications Open" : "Applications Closed"
                  }
                  sx={{
                    fontWeight: 800,
                    borderRadius: 2,
                    backgroundColor: isAppOpen
                      ? "rgba(93,193,175,0.25)"
                      : "rgba(217,83,79,0.15)",
                  }}
                />
              )}
            </Stack>
          </Box>

          <Box sx={{ p: 2.25 }}>
            {/* CONTENT */}
            {isRedirecting ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  py: 8,
                }}
              >
                <CircularProgress size={48} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Logging you in...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Redirecting to your dashboard
                </Typography>
              </Box>
            ) : isForgotPassword ? (
              <Box
                component="form"
                onSubmit={handleSubmitForgotPassword}
                sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
              >
                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  required
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  error={forgotEmailError}
                  helperText={
                    forgotEmailError ? "Please enter a valid email." : " "
                  }
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={isForgotPasswordLoading}
                  sx={primaryButtonSx}
                >
                  {isForgotPasswordLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    "Send Reset Email"
                  )}
                </Button>

                <Button
                  type="button"
                  variant="text"
                  onClick={() => setIsForgotPassword(false)}
                  sx={secondaryButtonSx}
                >
                  Back to Login
                </Button>
              </Box>
            ) : isSignup ? (
              <Box
                component="form"
                onSubmit={handleSignupSubmit}
                sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
              >
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                  <TextField
                    label="First Name"
                    variant="outlined"
                    fullWidth
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <TextField
                    label="Last Name"
                    variant="outlined"
                    fullWidth
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Stack>

                <FormSelect
                  label="School"
                  id="schoolId"
                  value={schoolId}
                  onChange={(e) => setSchoolId(e.target.value)}
                  required
                  fullWidth
                >
                  <MenuItem value="">
                    <em>Select School</em>
                  </MenuItem>
                  <MenuItem value="1">Alternate</MenuItem>
                  <MenuItem value="12">Cedar</MenuItem>
                  <MenuItem value="4">Dover Bay Secondary</MenuItem>
                  <MenuItem value="5">IslandConnectED</MenuItem>
                  <MenuItem value="2">John Barsby Community Secondary</MenuItem>
                  <MenuItem value="6">Ladysmith Secondary</MenuItem>
                  <MenuItem value="7">Nanaimo District Secondary</MenuItem>
                  <MenuItem value="9">Wellington Secondary</MenuItem>
                </FormSelect>

                <TextField
                  label="Email"
                  variant="outlined"
                  fullWidth
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={isSignup && isBlockedEmailDomain(email)}
                  helperText={
                    isSignup && isBlockedEmailDomain(email)
                      ? "School emails (@learn68.ca) are not allowed. Use a personal email."
                      : " "
                  }
                />

                <TextField
                  label="Confirm Email"
                  type="email"
                  variant="outlined"
                  fullWidth
                  required
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  error={isSignup && isBlockedEmailDomain(confirmEmail)}
                  helperText={
                    isSignup && isBlockedEmailDomain(confirmEmail)
                      ? "School emails (@learn68.ca) are not allowed. Use a personal email."
                      : " "
                  }
                  InputProps={{
                    onPaste: (e) => e.preventDefault(),
                    onCopy: (e) => e.preventDefault(),
                  }}
                />

                <TextField
                  label="Password"
                  type={showSignupPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() =>
                            setShowSignupPassword(!showSignupPassword)
                          }
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showSignupPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Confirm Password"
                  type={showSignupConfirmPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  InputProps={{
                    onPaste: (e) => e.preventDefault(),
                    onCopy: (e) => e.preventDefault(),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle confirm password visibility"
                          onClick={() =>
                            setShowSignupConfirmPassword(
                              !showSignupConfirmPassword
                            )
                          }
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showSignupConfirmPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Divider sx={{ my: 0.5 }} />

                <Stack direction="row" spacing={1}>
                  <Button
                    type="button"
                    variant="text"
                    onClick={() => setIsSignup(false)}
                    fullWidth
                    sx={secondaryButtonSx}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={primaryButtonSx}
                    disabled={
                      isSignupLoading ||
                      !firstName.trim() ||
                      !lastName.trim() ||
                      !email.trim() ||
                      !confirmEmail.trim() ||
                      !password.trim() ||
                      !confirmPassword.trim() ||
                      !schoolId ||
                      email.toLowerCase() !== confirmEmail.toLowerCase() ||
                      password !== confirmPassword ||
                      isBlockedEmailDomain(email) ||
                      isBlockedEmailDomain(confirmEmail)
                    }
                  >
                    {isSignupLoading ? "Creating..." : "Create Account"}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}
              >
                <TextField
                  inputRef={emailInputRef}
                  label="Email"
                  variant="outlined"
                  fullWidth
                  required
                  name="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => {
                    const newEmail = e.target.value;
                    emailStateRef.current = newEmail;
                    setEmail(newEmail);
                    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail);
                    setEmailError(!isValid);
                  }}
                  onFocus={syncLoginAutofillState}
                  onAnimationStart={handleLoginAutofillAnimationStart}
                  error={emailError}
                  helperText={emailError ? "Please enter a valid email." : " "}
                  sx={{
                    "& input:-webkit-autofill": {
                      animationName: "loginAutoFillStart",
                      animationDuration: "0.01s",
                    },
                  }}
                />

                <TextField
                  inputRef={passwordInputRef}
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  variant="outlined"
                  fullWidth
                  required
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    const newPassword = e.target.value;
                    passwordStateRef.current = newPassword;
                    setPassword(newPassword);
                    const isValid = newPassword?.trim() !== "";
                    setPasswordError(!isValid);
                  }}
                  onFocus={syncLoginAutofillState}
                  onAnimationStart={handleLoginAutofillAnimationStart}
                  error={passwordError}
                  helperText={passwordError ? "Password cannot be empty." : " "}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={() => setShowPassword((prev) => !prev)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& input:-webkit-autofill": {
                      animationName: "loginAutoFillStart",
                      animationDuration: "0.01s",
                    },
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={primaryButtonSx}
                  startIcon={isLoading && <CircularProgress size={18} />}
                  disabled={isLoginDisabled}
                >
                  {isLoading ? "Signing In..." : "Sign In"}
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    pt: 0.25,
                  }}
                >
                  <Link
                    component="button"
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                    underline="hover"
                    sx={{ fontWeight: 700, fontSize: "0.9rem" }}
                  >
                    Forgot email or password?
                  </Link>

                  {isAppOpen && (
                    <Link
                      component="button"
                      type="button"
                      onClick={() => setIsSignup(true)}
                      underline="hover"
                      sx={{ fontWeight: 800, fontSize: "0.9rem" }}
                    >
                      Create account
                    </Link>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </Paper>

        {/* RIGHT: Info panel */}
        {showRightInfoPanel && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              p: 2.25,
            }}
          >
            <Stack spacing={2}>
              {!isAppOpen ? (
                <>
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: "error.main" }}>
                      The Application Process is Currently Closed
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.75 }}
                    >
                      You will be able to create an account and apply for
                      awards, scholarships and bursaries when the application
                      process opens. This system supports general awards and
                      district/authority scholarships.
                    </Typography>

                    {(new Date(generalOpen) > new Date() ||
                      new Date(dasOpen) > new Date()) && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography sx={{ fontWeight: 800 }}>
                          Application Process will Open:
                        </Typography>

                        <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                          <Typography variant="body2">
                            <Box
                              component="span"
                              sx={{ color: "text.secondary" }}
                            >
                              General:{" "}
                            </Box>
                            <Box
                              component="span"
                              sx={{ color: "error.main", fontWeight: 900 }}
                            >
                              {formattedOpenDate(generalOpen)}
                            </Box>
                          </Typography>

                          <Typography variant="body2">
                            <Box
                              component="span"
                              sx={{ color: "text.secondary" }}
                            >
                              District/Authority:{" "}
                            </Box>
                            <Box
                              component="span"
                              sx={{ color: "error.main", fontWeight: 900 }}
                            >
                              {formattedOpenDate(dasOpen)}
                            </Box>
                          </Typography>
                        </Stack>
                      </Box>
                    )}
                  </Box>

                  <Divider />
                </>
              ) : (
                <>
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>
                      New to the Student Award System?
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.75 }}
                    >
                      Create one account and apply for general awards and/or
                      district/authority scholarships.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                      gap: 1.25,
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        p: 1.5,
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                        General
                      </Typography>
                      {isGenAppOpen ? (
                        <Typography variant="body2" color="text.secondary">
                          Apply for general scholarships, awards and bursaries.
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: "error.main", fontWeight: 900 }}
                        >
                          Closed
                        </Typography>
                      )}
                    </Paper>

                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        p: 1.5,
                      }}
                    >
                      <Typography sx={{ fontWeight: 900, mb: 0.5 }}>
                        District/Authority
                      </Typography>
                      {isDASAppOpen ? (
                        <Typography variant="body2" color="text.secondary">
                          Apply for District/Authority Scholarships.
                        </Typography>
                      ) : (
                        <Typography
                          variant="body2"
                          sx={{ color: "error.main", fontWeight: 900 }}
                        >
                          Closed
                        </Typography>
                      )}
                    </Paper>
                  </Box>

                  <Button
                    type="button"
                    variant="contained"
                    onClick={() => setIsSignup(true)}
                    sx={{
                      ...primaryButtonSx,
                      width: "100%",
                      backgroundColor: "#d9534f",
                      "&:hover": { backgroundColor: "#c64541" },
                    }}
                  >
                    Create an Account
                  </Button>

                  <Divider />
                </>
              )}

              {/* Claiming funds box */}
              <Box
                sx={{
                  borderRadius: 2,
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  background:
                    "linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.00))",
                }}
              >
                <Typography
                  sx={{ color: "error.main", fontWeight: 900, mb: 1 }}
                >
                  PROCEDURE FOR RELEASE OF FUNDS – CLAIMING YOUR AWARD
                </Typography>

                <Typography variant="body2" sx={{ mb: 1 }}>
                  To claim your award, please sign in and upload your proof of
                  registration.
                </Typography>

                <Typography variant="body2" sx={{ mb: 1 }}>
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    Accepted proof of registration includes{" "}
                  </Box>
                  a current timetable, a letter of registration or enrolment, or
                  a tuition statement that clearly displays both your name and
                  the name of your post-secondary institution.
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ color: "error.main", fontWeight: 900, mb: 1 }}
                >
                  Note: A Letter of Acceptance is not sufficient, and phone
                  screenshots will not be accepted.
                </Typography>

                <Typography variant="body2" sx={{ color: "error.main", mb: 1 }}>
                  Please do not use @learn68 or a school email address. Use your
                  personal email address.
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                  Once submitted, your award will be processed within
                  approximately 2 weeks.
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 900 }}>
                  Need help?
                </Typography>
                <Typography variant="body2">
                  <Link
                    href="mailto:awards@nlsf.ca"
                    underline="hover"
                    sx={{ fontWeight: 800 }}
                  >
                    awards@nlsf.ca
                  </Link>{" "}
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    | (250) 753-4074
                  </Box>
                </Typography>
              </Box>
            </Stack>
          </Paper>
        )}
      </Box>
    </Box>
  );
}

export default memo(ApplicationLoginBlock);
